import { Box } from '@mui/material';
import { Check, ChevronDown } from 'lucide-react';
import PickerMenu, { type PickerMenuEntry } from './PickerMenu';
import SuggestionNote from './SuggestionNote';

export type Choice = {
    value: string;
    label: string;
    /** A second line under the label in the menu. */
    detail?: string;
};

type ChoicePickerProps = {
    /** What is being chosen, read out with the current value. */
    name: string;
    choices: readonly Choice[];
    value: string | undefined;
    /** Shown when nothing is chosen, and names the menu entry that clears the choice. */
    placeholder: string;
    /** Shown for a value none of the choices is, such as one no longer offered; the value itself when absent. */
    unknownLabel?: string;
    /**
     * Set while the value is only a suggestion nobody chose: the value is then
     * drawn in italics over a note saying so, `label` is read out with it, and
     * `hint` explains it on hover.
     */
    suggestion?: { label: string; hint: string };
    onChange: (value: string | undefined) => void;
    disabled?: boolean;
};

/**
 * Picks one value from a short list, drawn as an outlined field.
 * A value the device only suggests is drawn in italics over a note saying so,
 * so it cannot be mistaken for one somebody chose.
 */
export default function ChoicePicker({
    name,
    choices,
    value,
    placeholder,
    unknownLabel,
    suggestion,
    onChange,
    disabled = false,
}: ChoicePickerProps) {
    const current = choices.find((choice) => choice.value === value);
    const suggested = current !== undefined ? suggestion : undefined;
    const label =
        value === undefined
            ? placeholder
            : (current?.label ?? unknownLabel ?? value);

    const entries: PickerMenuEntry[] = [
        { key: '', label: placeholder, onPick: () => onChange(undefined) },
        ...choices.map((choice) => ({
            key: choice.value,
            label: choice.label,
            detail: choice.detail,
            icon: choice.value === value ? <Check size={14} /> : undefined,
            selected: choice.value === value,
            onPick: () => onChange(choice.value),
        })),
    ];

    return (
        <>
            <PickerMenu
                field
                ariaLabel={`${name}: ${label}${
                    suggested ? ` (${suggested.label})` : ''
                }`}
                title={suggested?.hint}
                disabled={disabled}
                entries={entries}
                sx={{
                    width: '100%',
                    height: 28,
                    px: 1,
                    gap: 1,
                    fontSize: 12,
                    justifyContent: 'space-between',
                    color:
                        value === undefined ? 'text.secondary' : 'text.primary',
                    fontStyle: suggested ? 'italic' : undefined,
                }}
            >
                <Box
                    component="span"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </Box>
                <ChevronDown size={12} style={{ flexShrink: 0 }} />
            </PickerMenu>
            {suggested && (
                <SuggestionNote label={suggested.label} hint={suggested.hint} />
            )}
        </>
    );
}
